import React, { useMemo, useState, useRef, useEffect } from "react";
import { Wallet, Plus, Pencil, Trash2, CheckCircle2, Clock, Search, Tags, X, Paperclip, FileDown, FileText, Check, CalendarClock, AlertTriangle } from "lucide-react";
import { useApp, formatBRL, formatDate } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Autocomplete } from "../components/Autocomplete";
import { useToast } from "../hooks/use-toast";
import { gerarContasPagarPDF } from "../utils/financeiroPdf";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const hoje = () => new Date().toISOString().slice(0, 10);
const vazio = { fornecedorId: "", descricao: "", categoria: "Outros", valor: "", vencimento: hoje(), status: "pendente", dataPagamento: "", recorrente: false, comprovante: "", notaFiscal: "", observacoes: "" };
const fornVazio = { nome: "", documento: "", telefone: "", email: "", endereco: "", categoria: "", observacoes: "" };

// soma i meses a uma data ISO (YYYY-MM-DD), preservando o dia quando possível
const addMonthsISO = (iso, i) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const base = new Date(y, (m - 1) + i, 1);
  const ultimoDia = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  const dia = Math.min(d || 1, ultimoDia);
  return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
};

const ContasPagar = () => {
  const { contasPagar, contasReceber, fornecedores, categoriasConta, faturas, orcamentos, salvarConta, salvarOrcamentos, excluirConta, baixaMassaContas, excluirMassaContas, salvarCategoriaConta, updateCategoriaConta, excluirCategoriaConta, salvarFornecedor, gerarRecorrentes, empresa } = useApp();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(vazio);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("todos");
  const [fCat, setFCat] = useState("todas");
  const [sel, setSel] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const [novaCat, setNovaCat] = useState("");
  const [editCatId, setEditCatId] = useState(null);
  const [editCatNome, setEditCatNome] = useState("");
  const fileRef = useRef(null);
  const notaRef = useRef(null);

  // Parcelamento
  const [parcelado, setParcelado] = useState(false);
  const [numParcelas, setNumParcelas] = useState(2);
  const [parcelas, setParcelas] = useState([]);

  // Modais aninhados (criar categoria / fornecedor sem sair do cadastro)
  const [catInlineOpen, setCatInlineOpen] = useState(false);
  const [catInlineNome, setCatInlineNome] = useState("");
  const [fornInlineOpen, setFornInlineOpen] = useState(false);
  const [fornForm, setFornForm] = useState(fornVazio);
  const [fornExtra, setFornExtra] = useState([]); // fornecedores criados inline (disponíveis já no autocomplete)

  useEffect(() => { gerarRecorrentes(); /* eslint-disable-next-line */ }, []);

  const fornById = useMemo(() => Object.fromEntries((fornecedores || []).map((f) => [f.id, f])), [fornecedores]);

  const resetParcelamento = () => { setParcelado(false); setNumParcelas(2); setParcelas([]); };
  const abrirNovo = () => { setEditing(null); setForm(vazio); resetParcelamento(); setOpen(true); };
  const abrirEdit = (c) => { setEditing(c); setForm({ ...vazio, ...c, valor: c.valor || "" }); resetParcelamento(); setOpen(true); };

  const handleAnexo = (campo, ref) => (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, [campo]: reader.result }));
    reader.readAsDataURL(file);
    if (ref?.current) ref.current.value = "";
  };

  // Divide o valor total em N parcelas (ajusta a última p/ fechar a soma) e distribui vencimentos mensais
  const gerarParcelas = (n, totalStr, primeiroVenc) => {
    const total = parseFloat(totalStr) || 0;
    const qtd = Math.max(2, Math.min(parseInt(n) || 2, 120));
    const base = Math.floor((total / qtd) * 100) / 100;
    const arr = [];
    for (let i = 0; i < qtd; i++) {
      const valor = i === qtd - 1 ? +(total - base * (qtd - 1)).toFixed(2) : base;
      arr.push({ vencimento: addMonthsISO(primeiroVenc || hoje(), i), valor });
    }
    setParcelas(arr);
  };

  const togglarParcelado = (v) => {
    setParcelado(v);
    if (v) gerarParcelas(numParcelas, form.valor, form.vencimento);
    else setParcelas([]);
  };
  const setNum = (v) => { setNumParcelas(v); if (parcelado) gerarParcelas(v, form.valor, form.vencimento); };
  const setParcela = (i, patch) => setParcelas((ps) => ps.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const somaParcelas = useMemo(() => parcelas.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0), [parcelas]);

  const salvar = async () => {
    if (!form.descricao.trim()) { toast({ title: "Informe a descrição" }); return; }
    try {
      if (parcelado && !editing) {
        if (parcelas.length < 2) { toast({ title: "Gere ao menos 2 parcelas" }); return; }
        const payload = {
          fornecedorId: form.fornecedorId, descricao: form.descricao, categoria: form.categoria,
          notaFiscal: form.notaFiscal, observacoes: form.observacoes,
          parcelas: parcelas.map((p) => ({ vencimento: p.vencimento, valor: parseFloat(p.valor) || 0 })),
        };
        await salvarConta(payload);
        toast({ title: `${parcelas.length} parcelas lançadas` });
      } else {
        const payload = { ...form, valor: parseFloat(form.valor) || 0 };
        await salvarConta(editing ? { ...payload, id: editing.id } : payload);
        toast({ title: editing ? "Conta atualizada" : "Conta lançada" });
      }
      setOpen(false);
    } catch (e) { toast({ title: "Erro ao salvar", description: e?.response?.data?.detail || "" }); }
  };
  const [excluindo, setExcluindo] = useState(null);
  const remover = async (c) => {
    if (c.recorrente) { setExcluindo(c); return; }
    await excluirConta(c.id); toast({ title: "Conta excluída" });
  };
  const confirmarExclusao = async (escopo) => {
    const c = excluindo; setExcluindo(null);
    await excluirConta(c.id, escopo);
    toast({ title: escopo === "proximas" ? "Recorrência encerrada" : "Parcela excluída", description: escopo === "proximas" ? "Esta e as próximas parcelas pendentes foram removidas." : "As próximas parcelas continuam sendo geradas." });
  };

  const verAnexo = (dataUrl, titulo) => {
    if (!dataUrl) return;
    const w = window.open();
    if (!w) { toast({ title: "Permita pop-ups para ver o anexo" }); return; }
    const isPdf = dataUrl.startsWith("data:application/pdf");
    w.document.write(isPdf
      ? `<iframe src="${dataUrl}" style="border:0;width:100%;height:100vh"></iframe>`
      : `<img src="${dataUrl}" style="max-width:100%" />`);
    w.document.title = titulo;
  };

  const mesAtualIni = new Date().toISOString().slice(0, 8) + "01";
  const mesAtualFim = (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); })();
  const [fDe, setFDe] = useState("");
  const [fAte, setFAte] = useState("");
  const [porPagina, setPorPagina] = useState(20);
  const [pagina, setPagina] = useState(1);
  const periodoIni = fDe || fAte ? (fDe || "0000-01-01") : mesAtualIni;
  const periodoFim = fDe || fAte ? (fAte || "9999-12-31") : mesAtualFim;
  useEffect(() => { setPagina(1); }, [fDe, fAte, fStatus, fCat, q, porPagina]);
  const lista = useMemo(() => (contasPagar || []).filter((c) =>
    (c.vencimento || "") >= periodoIni && (c.vencimento || "") <= periodoFim &&
    (fStatus === "todos" || c.status === fStatus) &&
    (fCat === "todas" || c.categoria === fCat) &&
    (c.descricao.toLowerCase().includes(q.toLowerCase()) || (fornById[c.fornecedorId]?.nome || "").toLowerCase().includes(q.toLowerCase()))
  ).sort((a, b) => {
    const ap = a.status === "pendente", bp = b.status === "pendente";
    if (ap !== bp) return ap ? -1 : 1; // pendentes primeiro
    if (ap) return (a.vencimento || "").localeCompare(b.vencimento || ""); // vencimento mais próximo primeiro
    return (b.vencimento || "").localeCompare(a.vencimento || ""); // pagas: mais recentes primeiro
  }), [contasPagar, fStatus, fCat, q, fornById, periodoIni, periodoFim]);
  const totalPaginas = Math.max(1, Math.ceil(lista.length / porPagina));
  const listaPag = useMemo(() => lista.slice((pagina - 1) * porPagina, pagina * porPagina), [lista, pagina, porPagina]);

  const hj = hoje();
  const pendentes = useMemo(() => (contasPagar || []).filter((c) => c.status === "pendente"), [contasPagar]);
  const limiteBreve = useMemo(() => { const d = new Date(hj); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); }, [hj]);
  const vencendoEmBreve = useMemo(() => pendentes.filter((c) => c.vencimento && c.vencimento >= hj && c.vencimento <= limiteBreve).sort((a, b) => (a.vencimento || "").localeCompare(b.vencimento || "")), [pendentes, hj, limiteBreve]);
  const vencidas = useMemo(() => pendentes.filter((c) => c.vencimento && c.vencimento < hj).sort((a, b) => (a.vencimento || "").localeCompare(b.vencimento || "")), [pendentes, hj]);
  const totalBreve = vencendoEmBreve.reduce((s, c) => s + (c.valor || 0), 0);
  const totalVenc = vencidas.reduce((s, c) => s + (c.valor || 0), 0);
  const diasAte = (iso) => Math.round((new Date(iso) - new Date(hj)) / 86400000);
  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSel((s) => s.length === lista.length ? [] : lista.map((c) => c.id));
  const selPendentes = sel.filter((id) => (contasPagar || []).find((c) => c.id === id)?.status === "pendente");

  const baixarSelecionadas = async () => {
    if (selPendentes.length === 0) { toast({ title: "Selecione contas pendentes" }); return; }
    await baixaMassaContas(selPendentes);
    toast({ title: `${selPendentes.length} conta(s) marcada(s) como paga(s)` });
    setSel([]);
  };
  const excluirSelecionadas = async () => {
    if (sel.length === 0) return;
    await excluirMassaContas(sel);
    toast({ title: `${sel.length} conta(s) excluída(s)` });
    setSel([]);
  };

  const addCategoria = async () => {
    if (!novaCat.trim()) return;
    try { await salvarCategoriaConta(novaCat.trim()); setNovaCat(""); toast({ title: "Categoria adicionada" }); }
    catch (e) { toast({ title: "Erro", description: e?.response?.data?.detail || "" }); }
  };
  const salvarEdicaoCat = async () => {
    if (!editCatNome.trim()) return;
    try { await updateCategoriaConta(editCatId, editCatNome.trim()); setEditCatId(null); setEditCatNome(""); toast({ title: "Categoria atualizada" }); }
    catch (e) { toast({ title: "Erro", description: e?.response?.data?.detail || "" }); }
  };

  // Criar categoria inline (sem perder o cadastro da conta)
  const criarCategoriaInline = async () => {
    if (!catInlineNome.trim()) return;
    try {
      const cat = await salvarCategoriaConta(catInlineNome.trim());
      setForm((f) => ({ ...f, categoria: cat?.nome || catInlineNome.trim() }));
      setCatInlineNome(""); setCatInlineOpen(false);
      toast({ title: "Categoria criada" });
    } catch (e) { toast({ title: "Erro", description: e?.response?.data?.detail || "" }); }
  };
  // Criar fornecedor inline
  const criarFornecedorInline = async () => {
    if (!fornForm.nome.trim()) { toast({ title: "Informe o nome do fornecedor" }); return; }
    try {
      const forn = await salvarFornecedor(fornForm);
      if (forn?.id) { setFornExtra((xs) => [...xs, forn]); setForm((f) => ({ ...f, fornecedorId: forn.id })); }
      setFornForm(fornVazio); setFornInlineOpen(false);
      toast({ title: "Fornecedor cadastrado" });
    } catch (e) { toast({ title: "Erro", description: e?.response?.data?.detail || "" }); }
  };

  const fornItems = useMemo(() => {
    const base = (fornecedores || []).map((f) => ({ id: f.id, label: f.nome, sub: f.categoria || "" }));
    const ids = new Set(base.map((x) => x.id));
    const extra = fornExtra.filter((f) => !ids.has(f.id)).map((f) => ({ id: f.id, label: f.nome, sub: f.categoria || "" }));
    return [...base, ...extra];
  }, [fornecedores, fornExtra]);

  return (
    <div className="space-y-6" data-testid="contas-pagar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Contas a Pagar</h1>
          <p className="text-slate-400 text-sm mt-0.5">Despesas, fornecedores e vencimentos</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="gap-2" onClick={() => gerarContasPagarPDF({ empresa, contas: lista, fornById })} data-testid="pdf-contas-btn"><FileDown className="w-4 h-4" /> Relatório PDF</Button>
          <Button variant="outline" className="gap-2" onClick={() => setCatOpen(true)} data-testid="gerenciar-categorias-btn"><Tags className="w-4 h-4" /> Categorias</Button>
          <Button onClick={abrirNovo} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="nova-conta-btn"><Plus className="w-4 h-4" /> Nova Conta</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Box: Vencendo em breve */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="box-vencendo">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-amber-500" /> Vencendo em breve <span className="text-xs font-normal text-slate-400">(próximos 30 dias)</span></h2>
            <span className="text-sm font-bold text-amber-600">{formatBRL(totalBreve)}</span>
          </div>
          <div className="p-3 max-h-72 overflow-y-auto">
            {vencendoEmBreve.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Nenhuma conta a vencer. 🎉</p> : (
              <div className="space-y-2">
                {vencendoEmBreve.map((c) => {
                  const d = diasAte(c.vencimento);
                  return (
                    <div key={c.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-amber-50/50 border border-amber-100" data-testid={`breve-${c.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{c.descricao}</p>
                        <p className="text-xs text-slate-500">{fornById[c.fornecedorId]?.nome || c.categoria} · venc. {formatDate(c.vencimento)}</p>
                      </div>
                      <div className="text-right ml-3 shrink-0">
                        <p className="text-sm font-bold text-slate-700">{formatBRL(c.valor)}</p>
                        <span className="text-[11px] text-amber-600 font-medium">{d === 0 ? "vence hoje" : `em ${d} dia${d > 1 ? "s" : ""}`}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Box: Vencidas em aberto */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="box-vencidas">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Vencidas em aberto</h2>
            <span className="text-sm font-bold text-red-600">{formatBRL(totalVenc)}</span>
          </div>
          <div className="p-3 max-h-72 overflow-y-auto">
            {vencidas.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">Nenhuma conta vencida. 👍</p> : (
              <div className="space-y-2">
                {vencidas.map((c) => {
                  const d = Math.abs(diasAte(c.vencimento));
                  return (
                    <button key={c.id} onClick={() => abrirEdit(c)} className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-red-50/50 border border-red-100 hover:bg-red-50 text-left" data-testid={`vencida-${c.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{c.descricao}</p>
                        <p className="text-xs text-slate-500">{fornById[c.fornecedorId]?.nome || c.categoria} · venc. {formatDate(c.vencimento)}</p>
                      </div>
                      <div className="text-right ml-3 shrink-0">
                        <p className="text-sm font-bold text-red-600">{formatBRL(c.valor)}</p>
                        <span className="text-[11px] text-red-500 font-medium">{d} dia{d > 1 ? "s" : ""} em atraso</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 max-w-xs flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="flex-1 bg-transparent outline-none text-sm text-slate-700" data-testid="conta-busca" />
        </div>
        <div className="w-40"><Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="filtro-status-conta"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos status</SelectItem><SelectItem value="pendente">Pendentes</SelectItem><SelectItem value="pago">Pagas</SelectItem></SelectContent>
        </Select></div>
        <div className="w-48"><Select value={fCat} onValueChange={setFCat}>
          <SelectTrigger data-testid="filtro-categoria-conta"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="todas">Todas categorias</SelectItem>{(categoriasConta || []).map((c) => <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>)}</SelectContent>
        </Select></div>
      </div>
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-3 flex items-center gap-3 flex-wrap text-sm" data-testid="contas-periodo">
        <span className="font-medium text-slate-600">Período (vencimento):</span>
        <input type="date" value={fDe} onChange={(e) => setFDe(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5" data-testid="contas-de" />
        <span className="text-slate-400">até</span>
        <input type="date" value={fAte} onChange={(e) => setFAte(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5" data-testid="contas-ate" />
        {(fDe || fAte) ? <Button variant="ghost" size="sm" onClick={() => { setFDe(""); setFAte(""); }} data-testid="contas-mes-atual">Voltar ao mês atual</Button>
          : <span className="text-xs text-slate-400">Mostrando o mês atual ({mesAtualIni.split("-").reverse().join("/")} a {mesAtualFim.split("-").reverse().join("/")}). Use as datas para ver outros meses.</span>}
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
          <span>Por página</span>
          <select value={porPagina} onChange={(e) => setPorPagina(Number(e.target.value))} className="border border-slate-200 rounded-lg px-2 py-1 text-sm" data-testid="contas-por-pagina">
            {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span data-testid="contas-total">{lista.length} registro(s)</span>
        </div>
      </div>

      {sel.length > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5" data-testid="massa-toolbar">
          <span className="text-sm font-medium text-blue-800">{sel.length} selecionada(s)</span>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" onClick={baixarSelecionadas} data-testid="baixa-massa-btn"><CheckCircle2 className="w-4 h-4" /> Marcar como pagas</Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50" onClick={excluirSelecionadas} data-testid="excluir-massa-btn"><Trash2 className="w-4 h-4" /> Excluir</Button>
          <button className="ml-auto text-slate-400 hover:text-slate-600" onClick={() => setSel([])}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3"><input type="checkbox" checked={lista.length > 0 && sel.length === lista.length} onChange={toggleAll} data-testid="select-all-contas" /></th>
                <th className="px-4 py-3 font-medium">Descrição</th>
                <th className="px-4 py-3 font-medium">Fornecedor</th>
                <th className="px-4 py-3 font-medium">Categoria</th>
                <th className="px-4 py-3 font-medium">Vencimento</th>
                <th className="px-4 py-3 font-medium text-right">Valor</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {lista.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Nenhuma conta encontrada.</td></tr>}
              {listaPag.map((c) => {
                const vencida = c.status === "pendente" && c.vencimento && c.vencimento < hj;
                return (
                  <tr key={c.id} className="hover:bg-slate-50/60" data-testid={`conta-row-${c.id}`}>
                    <td className="px-4 py-3"><input type="checkbox" checked={sel.includes(c.id)} onChange={() => toggleSel(c.id)} data-testid={`select-conta-${c.id}`} /></td>
                    <td className="px-4 py-3 font-medium text-slate-700">
                      {c.descricao}
                      {c.recorrente && <span className="text-[10px] text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded ml-1">Recorrente</span>}
                      {c.parcelaTotal > 1 && <span className="text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded ml-1">Parcela {c.parcelaNum}/{c.parcelaTotal}</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{fornById[c.fornecedorId]?.nome || "-"}</td>
                    <td className="px-4 py-3"><span className="text-xs font-semibold px-2 py-1 rounded-md text-slate-600 bg-slate-100">{c.categoria}</span></td>
                    <td className={`px-4 py-3 ${vencida ? "text-red-600 font-medium" : "text-slate-500"}`}>{formatDate(c.vencimento)}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-700">{formatBRL(c.valor)}</td>
                    <td className="px-4 py-3">
                      {c.status === "pago"
                        ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Pago</span>
                        : <span className={`inline-flex items-center gap-1 text-xs font-semibold ${vencida ? "text-red-600" : "text-amber-600"}`}><Clock className="w-3.5 h-3.5" /> {vencida ? "Vencida" : "Pendente"}</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {c.notaFiscal && <button onClick={() => verAnexo(c.notaFiscal, `Nota Fiscal — ${c.descricao}`)} className="text-slate-400 hover:text-blue-600 p-1.5" title="Ver nota fiscal" data-testid={`nota-conta-${c.id}`}><FileText className="w-4 h-4" /></button>}
                      {c.comprovante && <button onClick={() => verAnexo(c.comprovante, `Comprovante — ${c.descricao}`)} className="text-slate-400 hover:text-emerald-600 p-1.5" title="Ver comprovante" data-testid={`comprovante-conta-${c.id}`}><Paperclip className="w-4 h-4" /></button>}
                      <button onClick={() => abrirEdit(c)} className="text-slate-400 hover:text-blue-600 p-1.5" data-testid={`editar-conta-${c.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remover(c)} className="text-slate-400 hover:text-red-600 p-1.5" data-testid={`excluir-conta-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm" data-testid="contas-paginacao">
            <span className="text-slate-500">Página {pagina} de {totalPaginas}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)} data-testid="contas-pag-anterior">Anterior</Button>
              {Array.from({ length: totalPaginas }, (_, i) => i + 1).filter((n) => n === 1 || n === totalPaginas || Math.abs(n - pagina) <= 2).map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && arr[idx - 1] !== n - 1 && <span className="px-1 text-slate-400">…</span>}
                  <button onClick={() => setPagina(n)} className={`min-w-8 h-8 px-2 rounded-md text-sm ${n === pagina ? "bg-blue-600 text-white" : "hover:bg-slate-100 text-slate-600"}`} data-testid={`contas-pag-${n}`}>{n}</button>
                </React.Fragment>
              ))}
              <Button variant="outline" size="sm" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)} data-testid="contas-pag-proxima">Próxima</Button>
            </div>
          </div>
        </div>
      </div>


      {/* Modal nova/editar conta */}
      <Dialog open={!!excluindo} onOpenChange={(o) => !o && setExcluindo(null)}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="excluir-recorrente-modal">
          <DialogHeader><DialogTitle>Excluir conta recorrente</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600"><b>{excluindo?.descricao}</b>{excluindo?.competencia ? ` — competência ${excluindo.competencia.split("-").reverse().join("/")}` : ""}. O que deseja excluir?</p>
          <div className="grid gap-2">
            <button type="button" onClick={() => confirmarExclusao("este")} className="text-left rounded-lg border border-slate-200 px-4 py-3 hover:bg-slate-50" data-testid="excluir-so-este">
              <span className="block text-sm font-medium text-slate-800">Só esta parcela</span>
              <span className="block text-xs text-slate-500">Remove apenas este mês; a recorrência continua nos próximos meses.</span>
            </button>
            <button type="button" onClick={() => confirmarExclusao("proximas")} className="text-left rounded-lg border border-red-200 bg-red-50/40 px-4 py-3 hover:bg-red-50" data-testid="excluir-proximas">
              <span className="block text-sm font-medium text-red-700">Esta e as próximas (encerrar recorrência)</span>
              <span className="block text-xs text-slate-500">Remove esta parcela e todas as futuras ainda pendentes; nada mais será gerado. Parcelas já pagas são mantidas.</span>
            </button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setExcluindo(null)}>Cancelar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="conta-modal">
          <DialogHeader><DialogTitle>{editing ? "Editar Conta" : "Nova Conta a Pagar"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Descrição</label>
              <input className={inputCls} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} data-testid="conta-descricao" /></div>

            {/* Fornecedor (autocomplete) + criar novo */}
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Fornecedor <span className="text-slate-400 font-normal">(opcional)</span></label>
              <div className="flex gap-2">
                <div className="flex-1"><Autocomplete testid="conta-fornecedor-busca" items={fornItems} value={form.fornecedorId} onChange={(id) => setForm({ ...form, fornecedorId: id })} placeholder="Buscar fornecedor... (deixe vazio se não houver)" /></div>
                <Button type="button" variant="outline" className="shrink-0 gap-1" onClick={() => { setFornForm(fornVazio); setFornInlineOpen(true); }} data-testid="novo-fornecedor-inline-btn"><Plus className="w-4 h-4" /> Novo</Button>
              </div>
            </div>

            {/* Categoria + criar nova */}
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Categoria</label>
              <div className="flex gap-2">
                <div className="flex-1"><Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger data-testid="conta-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">{(categoriasConta || []).map((c) => <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>)}{!(categoriasConta || []).some((c) => c.nome === "Outros") && <SelectItem value="Outros">Outros</SelectItem>}{form.categoria && form.categoria !== "Outros" && !(categoriasConta || []).some((c) => c.nome === form.categoria) && <SelectItem value={form.categoria}>{form.categoria}</SelectItem>}</SelectContent>
                </Select></div>
                <Button type="button" variant="outline" className="shrink-0 gap-1" onClick={() => { setCatInlineNome(""); setCatInlineOpen(true); }} data-testid="nova-categoria-inline-btn"><Plus className="w-4 h-4" /> Nova</Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">{parcelado ? "Valor total (R$)" : "Valor (R$)"}</label>
                <input type="number" step="0.01" className={inputCls} value={form.valor} onChange={(e) => { setForm({ ...form, valor: e.target.value }); if (parcelado) gerarParcelas(numParcelas, e.target.value, form.vencimento); }} data-testid="conta-valor" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">{parcelado ? "1º Vencimento" : "Vencimento"}</label>
                <input type="date" className={inputCls} value={form.vencimento} onChange={(e) => { setForm({ ...form, vencimento: e.target.value }); if (parcelado) gerarParcelas(numParcelas, form.valor, e.target.value); }} data-testid="conta-vencimento" /></div>
            </div>

            {/* Parcelamento (apenas na criação) */}
            {!editing && (
              <div className="border border-slate-200 rounded-lg p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={parcelado} onChange={(e) => togglarParcelado(e.target.checked)} data-testid="conta-parcelada" /> Conta parcelada
                </label>
                {parcelado && (
                  <>
                    <div className="flex items-end gap-2">
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Nº de parcelas</label>
                        <input type="number" min="2" max="120" className={`${inputCls} w-28`} value={numParcelas} onChange={(e) => setNum(e.target.value)} data-testid="conta-num-parcelas" />
                      </div>
                      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => gerarParcelas(numParcelas, form.valor, form.vencimento)} data-testid="conta-gerar-parcelas"><CheckCircle2 className="w-4 h-4" /> Dividir valor</Button>
                      <span className={`text-xs ml-auto ${Math.abs(somaParcelas - (parseFloat(form.valor) || 0)) > 0.01 ? "text-amber-600" : "text-emerald-600"}`} data-testid="conta-soma-parcelas">Soma: {formatBRL(somaParcelas)}</span>
                    </div>
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {parcelas.map((p, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`parcela-linha-${i}`}>
                          <span className="text-xs text-slate-500 w-8">{i + 1}ª</span>
                          <input type="date" className={`${inputCls} flex-1`} value={p.vencimento} onChange={(e) => setParcela(i, { vencimento: e.target.value })} data-testid={`parcela-venc-${i}`} />
                          <input type="number" step="0.01" className={`${inputCls} w-32`} value={p.valor} onChange={(e) => setParcela(i, { valor: e.target.value })} data-testid={`parcela-valor-${i}`} />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {!parcelado && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-600 mb-1 block">Status</label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="conta-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="pendente">Pendente</SelectItem><SelectItem value="pago">Pago</SelectItem></SelectContent>
                  </Select></div>
                {form.status === "pago" && (
                  <div><label className="text-sm font-medium text-slate-600 mb-1 block">Data do pagamento</label>
                    <input type="date" className={inputCls} value={form.dataPagamento || hoje()} onChange={(e) => setForm({ ...form, dataPagamento: e.target.value })} /></div>
                )}
              </div>
            )}

            {!parcelado && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.recorrente} onChange={(e) => setForm({ ...form, recorrente: e.target.checked })} data-testid="conta-recorrente" /> Conta recorrente (gera automaticamente todo mês — o valor pode ser ajustado em cada mês)
              </label>
            )}
            {!parcelado && editing?.recorrente && (
              <label className="flex items-start gap-2 text-sm text-slate-600 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
                <input type="checkbox" className="mt-0.5" checked={!!form.aplicarProximas} onChange={(e) => setForm({ ...form, aplicarProximas: e.target.checked })} data-testid="conta-aplicar-proximas" />
                <span><b>Aplicar às próximas parcelas</b> — replica fornecedor, descrição, categoria, valor, dia do vencimento e observações nas parcelas futuras ainda pendentes desta recorrência.</span>
              </label>
            )}

            {/* Anexos: Nota Fiscal + Comprovante */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Nota Fiscal (PDF/imagem)</label>
                <input ref={notaRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleAnexo("notaFiscal", notaRef)} data-testid="conta-nota-input" />
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" className="gap-2" onClick={() => notaRef.current?.click()} data-testid="conta-anexar-nota"><FileText className="w-4 h-4" /> Anexar NF</Button>
                  {form.notaFiscal && <span className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Anexada <button onClick={() => setForm({ ...form, notaFiscal: "" })} className="text-red-500 ml-1"><X className="w-3 h-3" /></button></span>}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Comprovante de pagamento</label>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleAnexo("comprovante", fileRef)} data-testid="conta-comprovante-input" />
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" className="gap-2" onClick={() => fileRef.current?.click()}><Paperclip className="w-4 h-4" /> Anexar</Button>
                  {form.comprovante && <span className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Anexado <button onClick={() => setForm({ ...form, comprovante: "" })} className="text-red-500 ml-1"><X className="w-3 h-3" /></button></span>}
                </div>
              </div>
            </div>

            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Observações</label>
              <textarea rows={2} className={inputCls} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={salvar} data-testid="salvar-conta">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal criar categoria inline */}
      <Dialog open={catInlineOpen} onOpenChange={setCatInlineOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm" data-testid="categoria-inline-modal">
          <DialogHeader><DialogTitle>Nova Categoria</DialogTitle></DialogHeader>
          <input className={inputCls} placeholder="Nome da categoria (ex.: Água, Luz)" value={catInlineNome} onChange={(e) => setCatInlineNome(e.target.value)} onKeyDown={(e) => e.key === "Enter" && criarCategoriaInline()} data-testid="categoria-inline-nome" autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatInlineOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={criarCategoriaInline} data-testid="salvar-categoria-inline">Salvar categoria</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal criar fornecedor inline */}
      <Dialog open={fornInlineOpen} onOpenChange={setFornInlineOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fornecedor-inline-modal">
          <DialogHeader><DialogTitle>Novo Fornecedor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Nome / Razão social</label>
              <input className={inputCls} value={fornForm.nome} onChange={(e) => setFornForm({ ...fornForm, nome: e.target.value })} data-testid="fornecedor-inline-nome" autoFocus /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">CNPJ / CPF</label>
                <input className={inputCls} value={fornForm.documento} onChange={(e) => setFornForm({ ...fornForm, documento: e.target.value })} placeholder="CNPJ ou CPF" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Categoria</label>
                <input className={inputCls} value={fornForm.categoria} onChange={(e) => setFornForm({ ...fornForm, categoria: e.target.value })} placeholder="ex.: Alimentos, Serviços" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Telefone</label>
                <input className={inputCls} value={fornForm.telefone} onChange={(e) => setFornForm({ ...fornForm, telefone: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">E-mail</label>
                <input type="email" className={inputCls} value={fornForm.email} onChange={(e) => setFornForm({ ...fornForm, email: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFornInlineOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={criarFornecedorInline} data-testid="salvar-fornecedor-inline">Salvar fornecedor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal categorias */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="categorias-conta-modal">
          <DialogHeader><DialogTitle>Categorias de Contas</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2">
            <input className={inputCls} placeholder="Nova categoria (ex.: Água, Luz)" value={novaCat} onChange={(e) => setNovaCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategoria()} data-testid="nova-categoria-conta" />
            <Button className="bg-blue-600 hover:bg-blue-700 shrink-0" onClick={addCategoria} data-testid="add-categoria-conta"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {(categoriasConta || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm gap-2" data-testid={`categoria-conta-${c.id}`}>
                {editCatId === c.id ? (
                  <>
                    <input className="flex-1 px-2 py-1 rounded border border-slate-200 outline-none focus:border-blue-500 text-sm" value={editCatNome} onChange={(e) => setEditCatNome(e.target.value)} onKeyDown={(e) => e.key === "Enter" && salvarEdicaoCat()} data-testid={`editar-categoria-input-${c.id}`} autoFocus />
                    <button onClick={salvarEdicaoCat} className="text-emerald-600 hover:text-emerald-700" title="Salvar" data-testid={`salvar-categoria-${c.id}`}><Check className="w-4 h-4" /></button>
                    <button onClick={() => { setEditCatId(null); setEditCatNome(""); }} className="text-slate-400 hover:text-slate-600" title="Cancelar"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <span className="text-slate-700 flex-1">{c.nome}</span>
                    <button onClick={() => { setEditCatId(c.id); setEditCatNome(c.nome); }} className="text-slate-400 hover:text-blue-600" title="Editar" data-testid={`editar-categoria-${c.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => excluirCategoriaConta(c.id)} className="text-slate-400 hover:text-red-600" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCatOpen(false)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContasPagar;
